import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { SalesAttributionUser } from '@/types/settings';

/**
 * NOT-509: active members eligible to own sales attribution — the Estimate
 * "Salesperson" and Job "Sales rep" pickers. Filtered server-side to users
 * whose role grants `estimates:create`, so finance-only roles (e.g. accountant)
 * never appear. Unlike `useUsers`, this needs no `users:view` permission, so a
 * sales rep still gets a populated dropdown.
 */
export function useSalesAttributionUsers(options?: {
  enabled?: boolean;
  /**
   * NOT-584: also return this user — the rep on a linked lead — if they may own
   * an estimate anywhere in the tenant. The list is otherwise scoped to the
   * caller's company, which in a multi-company tenant omits a lead rep the
   * conversion path would have credited. Callers read eligibility from this
   * list, so an omission reads as "ineligible" and silently moves the
   * commission to whoever opened the form.
   */
  includeUserId?: string | null;
}) {
  const includeUserId = options?.includeUserId || undefined;
  return useApiQuery<SalesAttributionUser[]>({
    queryKey: settingsKeys.salesAttributionCandidates(includeUserId),
    queryFn: () =>
      apiGet<SalesAttributionUser[]>(
        operationsClient,
        settingsEndpoints.salesAttributionCandidates(includeUserId),
      ),
    enabled: options?.enabled,
    showErrorToast: false,
  });
}
